import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { useDebounceFn, useSetState, useUpdateEffect } from 'ahooks';
import {
  Card,
  Justify,
  SearchBox,
  Segment,
  Table,
  Button,
  Bubble,
  ConfigProvider,
  Pagination,
} from 'tea-component';
import classNames from 'classnames';
import Image from 'rc-image';
import { isEmpty } from 'lodash-es';
import BubbleEllipsis from '@/pages/datalab/components/BubbleEllipsis';
import SvgIcon from '@home/components/SvgIcon';
import TextBubbles from '@home/components/TextBubbles';
import FilterSelect from "@/pages/dashboard/components/FilterSelect";
import Abnormal from '@home/components/Abnormal';
import Loading from '@/pages/intelligence/components/Loading';
import {
  RecentlyOrShareTableData,
  SubTableAndCard,
  SubTagType,
  TableDataTab,
} from './service';
import { DATALAB_EDITOR } from '@dbl/core/global';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { getShowCount } from '../../utils/common';
import { useGetDashBoardCreatorData, useGetHomeTabCount, useGetHomeTemplateData } from './hoooks';
import { useStore } from '@/pages/aigc/hooks/useContext/useStore';
import VideoDefault from '@/pages/home/assets/img/common/video-default.png';

import './index.scss';

// 页签标识
type TSubProps = {
  // 一级
  tab: TableDataTab;
  // 二级
  subTab: SubTagType;
  // 二级卡片
  subCardTab: SubTableAndCard;
};
// 组件props
type TRecentPropr ={
  // 业务code
  gameCode: string;
  // 权限拦截
  privilegesLink: (path: string, privileges: string[]) => void;
}
type TRecentSort <N= number, A= unknown> = {
  page: N ;
  page_size: N ;
  sort: A;
  creators: A;
}
// 报表打点id
const DASHBOARD_PAGE_ID = '601301';
// 图表打点id
const CHART_PAGE_ID = '600202';
// 模板打点id
const TEMPLATE_PAGE_ID = '601601';

// 聚合
type TRecentPageID = typeof DASHBOARD_PAGE_ID | typeof CHART_PAGE_ID | typeof TEMPLATE_PAGE_ID;

// 二级卡片数据源
// eslint-disable-next-line @typescript-eslint/naming-convention
export const subCardList: Record<string, string>[] = [
  {
    type: 'card',
    text: 'sub_card',
    key: 'card',
  },
  {
    type: 'sheet',
    text: 'sub_table',
    key: 'table',
  },
];

// 不同页面的名称和路径配置信息
const categoryMap: Record<TRecentPageID, {[key : string]: unknown;}> = {
  [DASHBOARD_PAGE_ID]: {
    cn: '报表',
    en: 'Dashboard',
    path: '/WorkBench/MyDashboard/ViewPage',
    privileges: [DATALAB_EDITOR.Dashbord],
  },
  [CHART_PAGE_ID]: {
    cn: '图表',
    en: 'Chart',
    path: '/Analysis/MultiAnalysis',
    privileges: [DATALAB_EDITOR.Chart],
  }, // 具体某个图表详情编辑状态
  [TEMPLATE_PAGE_ID]: {
    game_template: {
      en: 'Game Template',
      cn: '游戏模板',
    },
    public_template: {
      en: 'Public Template',
      cn: '公共模板',
    },
    path: '/WorkBench/MyDashboard/ViewPage',
    privileges: [DATALAB_EDITOR.Dashbord],
  },
};

// 中英文映射
const mapKey = {
  [TableDataTab.Template]: {
    name: 'template_name',
    date: 'dashboard_create_time',
    owner: 'dashboard_creator',
    visited: 'dashboard_visits'
  },
  [TableDataTab.Recent]: {
    date: 'table_recently_view_time',
    name: 'table_name',
    owner: 'table_owner'
  },
  [TableDataTab.Share]: {
    date: 'table_share_time',
    name: 'table_name',
    owner: 'table_owner'
  }
};

const { pageable, scrollable, sortable } = Table.addons;

export default function RecentlyAndShare({
  gameCode,
  privilegesLink,
}: TRecentPropr) {
  // i18n
  const { dblFormatMessage, commonFormatMessage, isEn } = useDblIntl('home');
  // 分页排序信息
  const [pageInfo, setPageInfo] = useSetState<TRecentSort>({
    page: 1,
    page_size: 10,
    sort: [],
    creators: []
  });
  // 搜索
  const [searchStr, setSearchStr] = useState('');
  const upRender = useRef(true);
  // 保存上次code
  const [lastCode, setLastCode] = useStore<string>('lastCode', gameCode);
  // 各级页签,切到其他目录，再次回来，需要保留上次页签
  const [tabCfg, setTabCfg] = useStore<TSubProps>('homeTemplateTab',{
    tab: '',
    subTab: SubTagType.All,
    subCardTab: SubTableAndCard.Card,
  });
  const { tab, subTab, subCardTab } = tabCfg || {};
  // 是否模板
  const isTemplate = tab === TableDataTab.Template;
  // 是否分享
  const isShareTab = tab === TableDataTab.Share;
  // 获取各页签总数
  const { data: tabCount } = useGetHomeTabCount({ game_code: gameCode, tab: 'home'}, (res: { recent_count: number; })=>{
    // 继承关系如下
    // 1：初始化进来，根据recent_count来决定命中哪个页签
    // 2：离开页面，进来展示上次的页面，需要继承
    // 3：切换code，需要取消继承，重新计算规则
    const isLastChangeCode = lastCode === gameCode;
    // 如果最近访问没有数据，展示模板中心，反之亦然
    const typeValue = !res?.recent_count ? TableDataTab.Template : TableDataTab.Recent;
    // 如果切换code，需要重新计算规则，否则，需要继承或者重新计算
    const setTabKey = !isLastChangeCode ? typeValue : tab || typeValue;
    // 各页签下分页数据
    const defaultParam = {
      [TableDataTab.Template]: 12,
      [TableDataTab.Recent]: 50,
      [TableDataTab.Share]: 10,
    }[setTabKey];
    // 改变tab视图
    setTabCfg((res)=>({...res,tab: setTabKey }));
    // 先初始化展示对应的参数
    setPageInfo({page_size: defaultParam, sort: defaultParam ===12 ? [{by: 'visited', order: 'desc'}] : [] });
  });
  // 重置默认值
  // 模板中心  12
  // 最近访问  50
  // 分析给我  10
  const defaultShare = isShareTab ? 10 : 50;
  const defaultPageSize = isTemplate ? 12 : defaultShare;
  // 是否报表且是卡片
  const isTemplateCard = isTemplate && subCardTab === SubTableAndCard.Card;
  const params = useMemo(() => ({
    game_code: gameCode,
    tab: isTemplate ? subTab : tab,
    ...pageInfo,
    sort: !isEmpty(pageInfo?.sort) ? pageInfo?.sort?.map((task: {by: string; order: string;})=>({key: task?.by,value: task?.order}))?.reverse() : []
  }),
  [gameCode, isTemplate, pageInfo, subTab, tab]);
  // 列表数据
  const { data, loading, error, mutate } = useGetHomeTemplateData(
    params,
    upRender,
  );
  // 获取创建人列表
  const { data: createList } = useGetDashBoardCreatorData(useMemo(()=>({tab: isTemplate ? subTab : tab, game_code: gameCode }),[gameCode, isTemplate, subTab, tab]), isTemplate);
  useEffect(() => {
    // 清空回调
    setSearchStr('');
  }, [tab]);

  useUpdateEffect(()=>{
    const isLastChangeCode = lastCode === gameCode;
    if(!isLastChangeCode){
      setTabCfg((res)=>({...res, tab: ''}));
    }
    // TODO: code变化，会导致上一次的数据还留存，渲染链路 旧数据=>loading=>新数据，下面直接每次都先清空数据，渲染链路 loading=>新数据
    // mutate({});
    return ()=>{
      setLastCode(gameCode);
    };
  },[gameCode]);
  
  // 当useGetHomeTemplateData函数中的params触发时，在获取tabCount时set了2次，会有可能导致触发2次接口,pageInfo变化的时候，在把渲染标识变成false，发起请求
  useUpdateEffect(()=>{
    upRender.current = false;
  },[pageInfo,tabCfg]);
  // 创建人列表查询
  const {run: handleOnClickCreateSearch} = useDebounceFn((value: Array<string>)=>{
    setPageInfo({creators: value});
  },{
    wait: 500
  });
  // 创建人列表
  const renderCreatesOptions = useMemo(
    () =>
      createList?.map((item) => ({
        value: item?.user_id,
        text: item?.user_name,
        title: item?.title,
      })),
    [data,createList],
  );
  // 列信息
  const columns = useMemo(() => {
    const listCfg = [
      {
        key: 'name',
        width: 600,
        header: dblFormatMessage(mapKey?.[tab]?.name),
        render(record: RecentlyOrShareTableData) {
          try {
            let jumpLink = categoryMap[record.page_id]?.path;
            let isAccess = true;
            switch (record.page_id) {
              case '601301': {
                // 报表详情页面
                jumpLink += `?dashboardId=${record?.id || ''}&dashboardName=${record?.name || ''}&isBack=true`;
                isAccess = !!record.read; // 拥有某个报表的只读权限时, 才能访问报表编辑页面
                break;
              }
              case '600202': {
                // 具体某个图表详情编辑状态
                jumpLink += `?from=edit&taskId=${
                  record.id
                }&name=${record?.name || ''}&nameCn=${record.name_cn || record?.name}`;
                isAccess = !!record.edit; // 图表只有编辑页面, 没有详情页面, 所以这里想要访问具体的图表页面需要有编辑权限
                break;
              }
              case '601601': {
                // 模版详情页面
                jumpLink += `?dashboardId=${record?.id || ''}&dashboardName=${record?.name || ''}&isBack=true`;
                isAccess = !!record.read; // 拥有某个报表的只读权限时, 才能访问报表编辑页面
                break;
              }
              default: {
                // 需求变更: 主页的Recent View只展示 报表的详情页面 和 具体的图表编辑页面(拖拉拽页面图表详情)
                // 如果返回其他 数据, 直接返回 横杆
                return '-';
              }
            }
            // Name展示的菜单中英文名称是打点信息中extInfo中上传的, 后端再返回给前端的, 用于主页中的表格前端搜索
            return (
              <Button
                className={'dbl-home-recent-table-name-btn'}
                type={'link'}
                onClick={() => {
                  privilegesLink(
                    jumpLink,
                    categoryMap[record.page_id].privileges,
                    isAccess,
                  );
                }}
              >
                <BubbleEllipsis
                  content={
                    (isEn ? record?.name : record?.name_cn) || record?.name
                  }
                />
              </Button>
            );
          } catch (e) {
            console.error('主页 表格 Name列错误:', e);
            return (isEn ? record?.name : record?.name_cn) || record?.name;
          }
        },
      },
      {
        key: 'category',
        width: 160,
        header: dblFormatMessage('table_category'),
        render(record: RecentlyOrShareTableData) {
          return categoryMap[record?.page_id]?.[record?.category]?.[isEn ? 'en' : 'cn'] ||  categoryMap[record?.page_id]?.[isEn ? 'en' : 'cn'] || '-';
        },
      },
      { key: 'date', width: 240, header: dblFormatMessage(mapKey?.[tab]?.date)},
      {
        key: 'owner',
        width: 200,
        header: isTemplate ? <TableHoverFiledSearchBox {
          ...{
            gameCode,
            data: renderCreatesOptions,
            value: pageInfo?.creators,
            handleOnClickCreateSearch,
            tab,
            buttonText: dblFormatMessage(mapKey?.[tab]?.owner)
          }
        }/> : dblFormatMessage(mapKey?.[tab]?.owner),
        render(record: RecentlyOrShareTableData) {
          return <BubbleEllipsis
            content={record?.owner}
          />;
        }
      },
    ];
    if(isTemplate){
      const newItem =  {
        key: 'visited',
        width: 160,
        align: 'right',
        header: dblFormatMessage(mapKey?.[tab]?.visited),
      };
      // 在数组的第二项（索引为1）插入 newItem
      listCfg.splice(2, 0, newItem);
    }
    return listCfg;
  }, [dblFormatMessage, gameCode, isEn, isTemplate, pageInfo?.creators, privilegesLink, renderCreatesOptions, tab]);
  // 排序默认态
  const defaultSort = useMemo(() => {
    const commonSort = [
      { key: 'category', prefer: 'desc' },
      { key: 'date', prefer: 'desc' },
    ];
    const commonPageSize = [10, 20, 30, 50, 100];

    const templateSort = [{ key: 'visited', prefer: 'desc' }];
    const templatePageSize = [12, 24, 36, 48, 60];

    const mapKey = {
      [TableDataTab.Template]: [templateSort, templatePageSize],
      [TableDataTab.Recent]: [commonSort, commonPageSize],
      [TableDataTab.Share]: [commonSort, commonPageSize],
    };
    return mapKey?.[tab];
  }, [tab]);
  // tab点击回调
  const handleOnSelectTab = <V extends {[key in string]: unknown}>(val: V) => {
    const { isRefresh, keyword, key, value } = val;
    // 是否在模板中心
    const currentTemplate = (value === TableDataTab.Template && key === 'tab') || key === 'subTab';
    // 是否在最近访问
    const currentTRecently = key === 'tab' && value === TableDataTab.Recent;
    let pageSizeCfg = 10;
    if(currentTemplate){
      pageSizeCfg = 12;
    }else if(currentTRecently){
      pageSizeCfg = 50;
    }
    setTabCfg((res) => ({ ...res, subTab: res?.tab === TableDataTab.Template ? res?.subTab : SubTagType.All , [key]: value }));
    if(isRefresh){
      setPageInfo(()=>({creators: [], keyword: keyword || '', page: 1, page_size: pageSizeCfg , sort: currentTemplate ? [{by: 'visited', order: 'desc' }] : [] }));
    }
    if(key !=='subCardTab'){
      // 切换，清空数据缓存，重置0
      mutate({});
    }
  };
  // 搜索回调
  const handleOnSearchBox = <S extends string>(text: S ) => {
    const keyword = text?.trim()?.toLowerCase();
    setPageInfo(() => ({creators: [], page: 1, page_size: defaultPageSize, keyword }));
  };
  // 搜索文本输出 
  const handleOnChangeSearchBox = (...[text]: Parameters<typeof handleOnSearchBox>) => {
    const keyword = text?.trim()?.toLowerCase();
    // 删除全部搜索字符等于清空操作
    if (!keyword?.length) {
      handleOnClearAllSearchBox();
    }
    setSearchStr(keyword);
  };
  // 搜索清空回调
  const handleOnClearAllSearchBox = () => {
    setSearchStr('');
    setPageInfo(() => ({creators: [], page: 1, page_size: defaultPageSize, keyword: '' }));
  };
  // 排序回调
  const handleOnChangeSort = (sort: { order: string | number; }[]) => {
    setPageInfo(() => ({ sort }));
  };
  // 分页回调
  const handleOnPagingChange = (query: { pageIndex: number; pageSize: number; }) => {
    const { pageIndex, pageSize } = query;
    setPageInfo(() => ({ page: pageIndex, page_size: pageSize }));
  };
  return (
    <>
      <Segment
        className={'dbl-home-tab-justify-mb'}
        options={[
          {
            value: TableDataTab.Template,
            text: dblFormatMessage('template_center', {
              count: getShowCount(subTab === SubTagType.All ? tabCount?.template_count || 0 : tabCount?.template_count ||  data?.count || 0, true),
            }),
          },
          {
            value: TableDataTab.Recent,
            text: dblFormatMessage('recently_view'),
          },
          {
            value: TableDataTab.Share,
            text: dblFormatMessage('share_with_me', {
              count: getShowCount(isShareTab && data?.count || tabCount?.share_count || 0, true),
            }),
          },
        ]}
        value={tab}
        onChange={(value) => handleOnSelectTab({key: 'tab', value, isRefresh: true})}
      />
      <Card>
        <Card.Body>
          <Justify
            left={
              isTemplate && (
                <Segment
                  className='dbl-home-tab-justify-sub'
                  options={[
                    {
                      value: SubTagType.All,
                      text: dblFormatMessage('sub_all', {
                        count: getShowCount(tabCount?.game_template_count + tabCount?.public_template_count || 0, true),
                      }),
                    },
                    {
                      value: SubTagType.Game,
                      text: dblFormatMessage('sub_game', {
                        count: getShowCount(tabCount?.game_template_count || 0, true),
                      }),
                    },
                    {
                      value: SubTagType.Public,
                      text: dblFormatMessage('sub_public', {
                        count: getShowCount(tabCount?.public_template_count || 0, true),
                      }),
                    },
                  ]}
                  value={subTab}
                  onChange={(value) => handleOnSelectTab({key: 'subTab', value, isRefresh: true, keyword: searchStr})}
                />
              )
            }
            right={
              <div className="dbl-home-search-container">
                <SearchBox
                  value={searchStr}
                  className={'dbl-home-recent-search'}
                  placeholder={tab === TableDataTab.Template ? dblFormatMessage('search_template_placeholder') : dblFormatMessage('search_dashboard_placeholder')}
                  onChange={handleOnChangeSearchBox}
                  onSearch={handleOnSearchBox}
                  onClear={handleOnClearAllSearchBox}
                />
                {isTemplate && (
                  <>
                    {subCardList.map((task) => (
                      <Bubble
                        dark
                        key={task?.type}
                        content={dblFormatMessage(task?.text)}
                      >
                        <i
                          onClick={() =>
                            handleOnSelectTab({ key: 'subCardTab', value: task?.key})
                          }
                          className={classNames(
                            'dbl-home-search-container-icon',
                            {
                              'dbl-home-search-container-active':
                                subCardTab === task.key,
                            },
                          )}
                        >
                          <SvgIcon type={task?.type} />
                        </i>
                      </Bubble>
                    ))}
                  </>
                )}
              </div>
            }
          />
          <TabSelectContainer
            records={data?.list}
            total={data?.count}
            loading={loading || isEmpty(data)}
            error={error}
            isEn={isEn}
            dblFormatMessage={dblFormatMessage}
            commonFormatMessage={commonFormatMessage}
            isTemplateCard={isTemplateCard}
            isTemplate={isTemplate}
            columns={columns}
            pageCfg={{
              pageInfo,
              defaultSort,
              setPageInfo,
            }}
            handleOnChangeSort={handleOnChangeSort}
            handleOnPagingChange={handleOnPagingChange}
            privilegesLink={privilegesLink}
          />
        </Card.Body>
      </Card>
    </>
  );
}

function TabTable({
  records,
  total,
  loading,
  error,
  commonFormatMessage,
  columns,
  pageCfg,
  handleOnChangeSort,
  handleOnPagingChange,
  isTemplate,
  isEn
}: any) {
  
  const { pageInfo, defaultSort } = pageCfg;
  const { sort } = pageInfo;
  const [defaultSortColumns, pageSizeOptions] = defaultSort || [];
  const paginationVisibleCount = isTemplate ? 12 : 10;
 
  return (
    <Abnormal
      style={{
        width: '100%',
        height: '560px',
      }}
      isLoading={loading}
      isError={error}
      nodataMsg={commonFormatMessage('no_data2')}
      isNoData={loading || error ? false : !records?.length}
      component={
        <ConfigProvider locale={isEn ? 'en' : 'zh'}>
          <Table
            className={'dbl-home-recent-table'}
            columns={columns}
            records={records}
            addons={[
              scrollable({
                maxHeight: '510px',
                scrollToTopOnChange: true,
              }),
              pageable({
                recordCount: total,
                stateText: commonFormatMessage('total', {
                  total
                }),
                pageSize: pageInfo?.page_size,
                pageIndex: pageInfo?.page,
                stateTextVisible: total > paginationVisibleCount,
                pageSizeVisible: total > paginationVisibleCount,
                pageIndexVisible: total > paginationVisibleCount,
                jumpVisible: total > paginationVisibleCount,
                endJumpVisible: total > paginationVisibleCount,
                pageSizeOptions,
                onPagingChange: handleOnPagingChange,
              }),
              sortable({
                columns: defaultSortColumns,
                value: sort || [],
                onChange: handleOnChangeSort,
              }),
            ]}
          />
        </ConfigProvider>
      }
    />
  );
}

const TabSelectContainer = (props: any) =>
  props?.isTemplateCard ? (
    <TemplateCardContainer {...props} />
  ) : (
    <TabTable {...props} />
  );

const TemplateCardContainer = (props: any) => {
  const {
    dblFormatMessage,
    total,
    pageCfg,
    handleOnPagingChange,
    records,
    privilegesLink,
    loading,
    error,
    commonFormatMessage,
    isEn
  } = props;
  const { defaultSort, pageInfo } = pageCfg;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [defaultSortColumns, pageSizeOptions] = defaultSort;
  const dataSource = records?.sort((a: { visited: number; }, b: { visited: number; })=> b?.visited - a?.visited);
  const handleOnOpenViewDetail = (task: any)=>{
    let jumpLink = categoryMap[task.page_id]?.path;
    jumpLink += `?dashboardId=${task?.id || ''}&dashboardName=${task?.name || ''}&isBack=${true}`;
    privilegesLink(
      jumpLink,
      categoryMap[task.page_id].privileges,
      task?.read,
    );
  };
  return (
    <Abnormal
      style={{
        width: '100%',
        height: '560px',
      }}
      isLoading={loading}
      isError={error}
      nodataMsg={commonFormatMessage('no_data2')}
      isNoData={loading || error ? false : !records?.length}
      component={
        <>
          <div className={'dbl-home-template-card-container'}>
            {dataSource?.map((task: any) => (
              <>
                <div
                  className="dbl-home-template-card-container-item"
                  onClick={()=>handleOnOpenViewDetail(task)}
                >
                  <div className={'dbl-home-template-card-container-top'}>
                    <Image
                      className={classNames(
                        "dbl-template-image",
                        {
                          'dbl-template-image-empty':
                          !task?.preview_image_url,
                        },
                      )}
                      key={`video-thumb-${task?.company_id}`}
                      src={task?.preview_image_url || VideoDefault}
                      placeholder={
                        <div style={{ height: '145px', background: '#e0eeff' }}>
                          <Loading />
                        </div>
                      }
                      preview={false}
                      fallback={VideoDefault}
                      onError={(e: any) => {
                        e.target.src = VideoDefault;
                        e.target.style.position = 'absolute';
                        e.target.style.top = '50%';
                        e.target.style.left = '50%';
                        e.target.style.transform = 'translate(-50%, -50%)';
                      }}
                    />
                  </div>
                  <div className={'dbl-home-template-card-container-bottom'}>
                    <div className={'dbl-home-template-card-container-bottom-name'}>
                      <TextBubbles
                        className="text-exceeds-bubble"
                        text={task?.template_title}
                      />
                    </div>
                    <div className={'dbl-home-template-card-container-bottom-info'}>
                      <SvgIcon
                        type="open-eyes"
                        className="dbl-home-template-card-container-bottom-info-eyes"
                      />
                      <div className="dbl-home-template-card-container-bottom-info-count">
                        {getShowCount(task?.visited, true)}
                      </div>
                      <div className="dbl-home-template-card-container-bottom-info-createBy">
                        {dblFormatMessage('created_by')}&nbsp;{task?.owner}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ))}
          </div> 
          <ConfigProvider locale={isEn ? 'en' : 'zh'}>
            <Pagination
              recordCount={total}
              stateText={
                commonFormatMessage('total', {
                  total
                })
              }
              stateTextVisible={total > 12}
              pageSizeVisible={total > 12}
              pageIndexVisible={total > 12}
              jumpVisible={total > 12}
              endJumpVisible={total > 12}
              pageSizeOptions={pageSizeOptions}
              pageIndex={pageInfo.page}
              pageSize={pageInfo.page_size}
              onPagingChange={handleOnPagingChange}
            />
          </ConfigProvider>
        </>
      }
    />
  );
};

export const TableHoverFiledSearchBox = memo((props: any)=>{
  const { buttonText, data, value, handleOnClickCreateSearch, listWidth} = props;
  const { formatMessage, locale } = useIntl();
  const [filterValue, setFilterValue] = useState(value);
  // 辨识本次是否是无效输入
  const upRenderRef = useRef(false);
  return(
    <div className='dbi-dashboard-create-multiple'>
      <FilterSelect
        listWidth={listWidth || 250}
        searchable
        options={data || []}
        value={filterValue}
        button={
          <div 
            style={{color: (isEmpty(value) || (!upRenderRef.current && data?.length === filterValue?.length)) ? '#262832' : '#326ff0'}}
          >
            {buttonText}
            <SvgIcon style={
              {
                paddingLeft: '5px',
                width: '12px',
                height: '12px',
              }
            }type="funnel"/>
          </div>
        }
        appearance="pure"
        onChange={(res)=>{
          upRenderRef.current = true;
          setFilterValue(res);
        }}
        onClose={()=>{
          upRenderRef.current && handleOnClickCreateSearch?.(filterValue);
          upRenderRef.current = false;
        }}
        placeholder={formatMessage({id: 'dbl_common_search'})}
        locale={locale}
        name={buttonText}
        clickClose={false}
      />
    </div>
  );
});